export const ENDPOINTS = {
  // ================= PATIENT AUTH =================
  PATIENT_SIGNUP: "/app/signup",
  PATIENT_LOGIN: "/app/login",
  SEND_OTP: "/app/send-otp",
  RESEND_OTP: "/app/resend-otp",
  VERIFY_OTP: "/app/verify-otp",
  COMPLETE_PROFILE: "/app/complete-profile",
  GET_ME: "/app/me",
  PATIENT_LOGOUT: "/app/logout",
  SET_PREFERRED_LANGUAGE: "/app/preferences/language",
  PROFILE_AVATAR_UPLOAD_URL: "/app/profile/avatar/upload-url",

  // ================= PATIENT FLOW =================
  // Prescriptions (S3 presigned flow)
  PRESCRIPTION_UPLOAD_URLS: "/app/prescriptions/upload-urls",
  /** POST { keys } — cleanup orphaned pending/ S3 objects after failed register step */
  PRESCRIPTION_DELETE_PENDING_KEYS: "/app/prescriptions/delete-pending-keys",
  UPLOAD_PRESCRIPTIONS: "/app/prescriptions",
  GET_MY_PRESCRIPTIONS: "/app/prescriptions",
  /** DELETE — removes DB row and S3 object (s3Key or key derived from fileUrl) */
  DELETE_PRESCRIPTION: (id) => `/app/prescriptions/${id}`,
  GET_CATEGORIES: "/app/categories",
  GET_SUBCATEGORIES: "/app/subcategories",
  CREATE_SELECTIONS: "/app/selections",
  GET_SELECTIONS: "/app/selections",

  SAVE_PATIENT_LOCATION: "/app/location",

  GET_LOWEST_PRICE: "/app/match-requests/lowest-price",
  GET_PREVIEW: "/app/match-requests/preview",
  CREATE_MATCH_REQUEST: "/app/match-requests",
  /** POST — DRAFT request (documents first; then category + submit) */
  CREATE_DRAFT_MATCH_REQUEST: "/app/match-requests/draft",
  /** POST — after category/price, submit for admin (replaces one-shot create for linear flow) */
  SUBMIT_MATCH_REQUEST: (matchRequestId) => `/app/match-requests/${matchRequestId}/submit`,
  /** POST — after documents verified, notify matched providers */
  OPEN_MATCH_REQUEST_BIDDING: (matchRequestId) => `/app/match-requests/${matchRequestId}/open-bidding`,
  /** GET — list patient's match requests (newest first) */
  LIST_MY_MATCH_REQUESTS: "/app/match-requests",
  UPDATE_MATCH_REQUEST: (matchRequestId) => `/app/match-requests/${matchRequestId}`,
  DELETE_MATCH_REQUEST: (matchRequestId) => `/app/match-requests/${matchRequestId}`,
  /** Prefer query form — works even when nested /:id/detail paths 404 behind some proxies. */
  GET_MATCH_REQUEST_DETAIL: (matchRequestId) =>
    `/app/match-requests/detail?id=${encodeURIComponent(matchRequestId)}`,
  GET_PROVIDER_COUNT: (matchRequestId) =>
    `/app/match-requests/${matchRequestId}/provider-count`,
  GET_MATCH_RESULTS: (matchRequestId) =>
    `/app/match-requests/${matchRequestId}/matches`,
  GET_LATEST_MATCH_REQUEST: "/app/match-requests/latest",
  GET_ACTIVE_MATCH_REQUEST: "/app/match-requests/active",
  /** Submitted (non-draft) MR exists? bidding published? draft id? — avoids confusing GET /latest (draft vs submitted). */
  GET_MATCH_REQUEST_SUBMISSION_STATUS: "/app/match-requests/submission-status",
  RESPOND_TO_BID: (bidId) => `/app/bids/${bidId}/respond`,
  PATIENT_COUNTER_BID: (bidId) => `/app/bids/${bidId}/counter`,
  GET_BID_CONVERSATION: (bidId) => `/app/bids/${bidId}/conversation`,
  PATIENT_TREATMENT_BILL: (bidId) => `/app/bids/${bidId}/treatment-bill`,
  PATIENT_TREATMENT_BILL_RESPOND: (bidId) => `/app/bids/${bidId}/treatment-bill/respond`,
  DOCTOR_TREATMENT_BILL: (bidId) => `/doctor/bids/${bidId}/treatment-bill`,
  HOSPITAL_TREATMENT_BILL: (bidId) => `/hospital/bids/${bidId}/treatment-bill`,
  DOCTOR_VERIFY_CLOSE_CASE: (bidId) => `/doctor/bids/${bidId}/verify-close-case`,
  HOSPITAL_VERIFY_CLOSE_CASE: (bidId) => `/hospital/bids/${bidId}/verify-close-case`,
  GET_BID_BOOKING_SLOTS: (bidId) => `/app/bids/${bidId}/booking-slots`,
  BID_SLOT_LOCK: (bidId) => `/app/bids/${bidId}/slot-lock`,

  /** Patient 1–5 star rating for an approved doctor or hospital (upsert). */
  SET_PROVIDER_RATING: (providerType, providerId) =>
    `/app/provider-ratings/${providerType}/${providerId}`,
  GET_MY_PROVIDER_RATING: (providerType, providerId) =>
    `/app/provider-ratings/my/${providerType}/${providerId}`,

  CREATE_CONVERSATION: "/app/conversations",
  GET_CONVERSATIONS: "/app/conversations",
  GET_CONVERSATION_BY_ID: (conversationId) =>
    `/app/conversations/${conversationId}`,
  GET_CONVERSATION_MESSAGES: (conversationId) =>
    `/app/conversations/${conversationId}/messages`,
  SEND_CONVERSATION_MESSAGE: (conversationId) =>
    `/app/conversations/${conversationId}/messages`,
  DOCTOR_SEND_CONVERSATION_MESSAGE: (conversationId) =>
    `/doctor/conversations/${conversationId}/messages`,
  HOSPITAL_SEND_CONVERSATION_MESSAGE: (conversationId) =>
    `/hospital/conversations/${conversationId}/messages`,

  BOOK_APPOINTMENT: "/app/appointments",
  GET_MY_APPOINTMENTS: "/app/appointments",
  UPDATE_PENDING_APPOINTMENT_SLOT: (appointmentId) => `/app/appointments/${appointmentId}/pending-slot`,
  PATIENT_NOTIFICATIONS: "/app/notifications",
  PATIENT_JOURNEY_SUMMARY: "/app/journey/summary",

  // Compliance / legal (patient)
  PATIENT_ACCEPT_LEGAL: "/app/legal/accept",
  PATIENT_DATA_EXPORT: "/app/me/data-export",
  PATIENT_DISPUTES: "/app/disputes",
  PATIENT_ANALYTICS_EVENTS: "/app/analytics/events",
  /** Public safe fields (versions, support phone, feature flags, etc.) */
  PLATFORM_SETTINGS_PUBLIC: "/app/platform-settings/public",

  // ================= DOCTOR FLOW =================
  DOCTOR_SIGNUP_UPLOAD_URLS: "/doctor/signup-upload-urls",
  DOCTOR_SIGNUP: "/doctor/signup",
  DOCTOR_VERIFY_OTP: "/doctor/verify-otp",
  DOCTOR_SEND_EMAIL_OTP: "/doctor/send-email-otp",
  DOCTOR_VERIFY_EMAIL_OTP: "/doctor/verify-email-otp",
  DOCTOR_RESEND_EMAIL_OTP: "/doctor/resend-email-otp",
  DOCTOR_LOGIN: "/doctor/login",
  DOCTOR_RESEND_OTP: "/doctor/resend-otp",
  DOCTOR_REFRESH_TOKEN: "/doctor/refresh-token",
  DOCTOR_ME: "/doctor/me",
  DOCTOR_REQUEST_PROFILE_EDIT: "/doctor/me/request-edit",
  DOCTOR_VERIFY_PROFILE_EDIT: "/doctor/me/verify-edit",
  DOCTOR_REQUEST_DEGREES_EDIT: "/doctor/me/degrees/request-edit",
  DOCTOR_CANCEL_DEGREES_EDIT: "/doctor/me/degrees/cancel-edit",
  DOCTOR_PROFILE_IMAGES_UPLOAD_URLS: "/doctor/profile/images/upload-urls",
  DOCTOR_PROFILE_IMAGES: "/doctor/profile/images",
  DOCTOR_UPDATE_AVAILABILITY: "/doctor/availability",
  /** POST { placeIds } — Google photo_reference list per place (server key); client builds Place Photo URLs with Maps key */
  DOCTOR_PLACE_PHOTO_REFS: "/doctor/places/photo-refs",
  HOSPITAL_SIGNUP_UPLOAD_URLS: "/hospital/signup-upload-urls",
  HOSPITAL_SIGNUP: "/hospital/signup",
  HOSPITAL_VERIFY_OTP: "/hospital/verify-otp",
  HOSPITAL_SEND_EMAIL_OTP: "/hospital/send-email-otp",
  HOSPITAL_VERIFY_EMAIL_OTP: "/hospital/verify-email-otp",
  HOSPITAL_RESEND_EMAIL_OTP: "/hospital/resend-email-otp",
  HOSPITAL_LOGIN: "/hospital/login",
  HOSPITAL_RESEND_OTP: "/hospital/resend-otp",
  HOSPITAL_REFRESH_TOKEN: "/hospital/refresh-token",
  HOSPITAL_ME: "/hospital/me",
  HOSPITAL_REQUEST_PROFILE_EDIT: "/hospital/me/request-edit",
  HOSPITAL_VERIFY_PROFILE_EDIT: "/hospital/me/verify-edit",
  HOSPITAL_UPDATE_AVAILABILITY: "/hospital/availability",
  GET_HOSPITAL_CATEGORIES: "/hospital/categories",
  GET_HOSPITAL_SUBCATEGORIES: "/hospital/subcategories",
  GET_HOSPITAL_SELECTIONS: "/hospital/selections",
  CREATE_HOSPITAL_SELECTION: "/hospital/selections",

  // Doctor Expertise
  GET_DOCTOR_CATEGORIES: "/doctor/categories",
  GET_DOCTOR_SUBCATEGORIES: "/doctor/subcategories",
  GET_DOCTOR_SELECTIONS: "/doctor/selections",
  CREATE_DOCTOR_SELECTION: "/doctor/selections",

  // Doctor Bids (patient bids assigned to doctor)
  DOCTOR_BIDS: "/doctor/bids",
  HOSPITAL_BIDS: "/hospital/bids",
  GET_DOCTOR_BID_CONVERSATION: (bidId) => `/doctor/bids/${bidId}/conversation`,
  GET_HOSPITAL_BID_CONVERSATION: (bidId) => `/hospital/bids/${bidId}/conversation`,
  /** Timeline messages (must use provider token — not `/app/conversations/...`). */
  GET_DOCTOR_CONVERSATION_MESSAGES: (conversationId) =>
    `/doctor/conversations/${conversationId}/messages`,
  GET_HOSPITAL_CONVERSATION_MESSAGES: (conversationId) =>
    `/hospital/conversations/${conversationId}/messages`,
  DOCTOR_BID_COUNTER: (bidId) => `/doctor/bids/${bidId}/counter`,
  DOCTOR_BID_ACCEPT: (bidId) => `/doctor/bids/${bidId}/accept-patient-price`,
  DOCTOR_BID_REJECT: (bidId) => `/doctor/bids/${bidId}/reject`,
  HOSPITAL_BID_COUNTER: (bidId) => `/hospital/bids/${bidId}/counter`,
  HOSPITAL_BID_ACCEPT: (bidId) => `/hospital/bids/${bidId}/accept-patient-price`,
  /** Authenticated inline preview only (no public URL; providers cannot download from API). */
  DOCTOR_PATIENT_DOCUMENT_PREVIEW: (prescriptionId) =>
    `/doctor/patient-documents/${prescriptionId}/preview`,
  HOSPITAL_PATIENT_DOCUMENT_PREVIEW: (prescriptionId) =>
    `/hospital/patient-documents/${prescriptionId}/preview`,

  // Doctor Notifications
  DOCTOR_NOTIFICATIONS: "/doctor/notifications",
  HOSPITAL_NOTIFICATIONS: "/hospital/notifications",

  // Doctor Appointments
  DOCTOR_APPOINTMENTS: "/doctor/appointments",
  DOCTOR_APPOINTMENT_RESPOND: (appointmentId) => `/doctor/appointments/${appointmentId}/respond`,
  DOCTOR_APPOINTMENT_COMPLETE: (appointmentId) => `/doctor/appointments/${appointmentId}/complete`,
  DOCTOR_APPOINTMENT_VISIT_OUTCOME: (appointmentId) => `/doctor/appointments/${appointmentId}/visit-outcome`,
  HOSPITAL_APPOINTMENTS: "/hospital/appointments",
  HOSPITAL_APPOINTMENT_RESPOND: (appointmentId) => `/hospital/appointments/${appointmentId}/respond`,
  HOSPITAL_APPOINTMENT_COMPLETE: (appointmentId) => `/hospital/appointments/${appointmentId}/complete`,
  HOSPITAL_APPOINTMENT_VISIT_OUTCOME: (appointmentId) => `/hospital/appointments/${appointmentId}/visit-outcome`,

  // ================= ADMIN =================
  CREATE_CATEGORY: "/admin/categories",
  CREATE_SUBCATEGORY: "/admin/subcategories",
};

export default ENDPOINTS;
